import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { mobileTopService } from '../services/mobileTopService';
import { mobileObjectDetailService } from '../services/mobileObjectDetailService';
import { useApiError } from '../../shared/hooks/useApiError';
import { MessageDialog } from '../../shared/ui';
import Footer from '../components/Footer';
import { useAuth } from '../../shared/auth';

// CSS-in-JS スタイル
const styles = {
  container: {
    minHeight: '100vh',
    background: 'white',
    position: 'relative',
    paddingBottom: '60px'
  },
  loading: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgba(255, 255, 255, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999
  },
  introduction: {
    display: 'none',
    height: '100%',
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    zIndex: 1000
  },
  introductionSlider: {
    margin: 0,
    padding: 0,
    listStyle: 'none'
  },
  introductionSliderLi: {
    textAlign: 'center'
  },
  introductionSliderImg: {
    maxWidth: '100%',
    height: 'auto'
  },
  startButton: {
    fontSize: '14pt',
    fontWeight: '900',
    width: '80%',
    background: '#f93535',
    color: 'white',
    border: 'none',
    borderRadius: '20px',
    letterSpacing: '10px',
    padding: '6px 0',
    margin: '40px auto',
    display: 'block',
    cursor: 'pointer'
  },
  contents: {
    display: 'block'
  },
  pixLabelLogo: {
    width: '100%',
    margin: '40px 0 30px 0'
  },
  news: {
    border: '1px solid #BBB',
    margin: '0 10px'
  },
  newsTitle: {
    background: '#BBB',
    color: '#FFF',
    padding: '2px 8px',
    fontSize: '8pt'
  },
  newsNaiyo: {
    padding: '3px 12px'
  },
  topInfo: {
    width: '100%'
  },
  topInfo1: {
    marginTop: '80px'
  },
  topInfo2: {
    marginTop: '40px'
  },
  topInfo3: {
    marginTop: '40px'
  },
  topInfo4: {
    marginTop: '40px'
  },
  topInfo5: {
    marginTop: '40px'
  },
  topInfo6: {
    margin: '40px 0 200px 0'
  },
  navHorizontal: {
    width: '100%',
    overflow: 'hidden',
    background: '#FFF',
    position: 'fixed',
    top: 0,
    height: '60px',
    zIndex: 1000
  },
  mask: {
    width: '100%',
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch'
  },
  list: {
    display: 'inline-table',
    maxWidth: '100%',
    textAlign: 'center',
    margin: 0,
    padding: 0,
    listStyle: 'none'
  },
  listLi: {
    display: 'table-cell',
    whiteSpace: 'nowrap',
    lineHeight: '1.4',
    padding: '16px 30px',
    height: '60px',
    cursor: 'pointer',
    backgroundColor: '#EEE',
    color: '#444',
    transition: 'background-color 0.2s ease'
  },
  objectList: {
    margin: '60px auto 120px auto',
    clear: 'both',
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    width: '100%',
    boxSizing: 'border-box',
    gap: '0'
  },
  objectDIV: {
    outline: '1px solid #DDD',
    outlineOffset: '-1px',
    background: '#2B2222',
    overflow: 'hidden',
    cursor: 'pointer',
    margin: '0',
    width: '100%',
    height: 'auto',
    boxSizing: 'border-box'
  },
  objectIdDIV: {
    fontSize: '8pt',
    fontWeight: '900',
    color: '#FFF',
    background: '#2B2222',
    padding: '2px 4px',
    height: '20px',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis'
  },
  objectInDIV: {
    overflow: 'hidden',
    aspectRatio: '1',
    width: '100%'
  },
  objectInDIVImg: {
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: 'contain'
  },
  loadObjectDataButtonArea: {
    display: 'block',
    textAlign: 'center',
    margin: '120px auto 200px auto'
  },
  loadObjectDataButton: {
    background: '#2B2222',
    color: '#FFF',
    padding: '10px 20px',
    borderRadius: '5px',
    cursor: 'pointer'
  },
  listContainer: {
    height: '100%',
    overflowY: 'scroll',
    WebkitOverflowScrolling: 'touch'
  },
  intro7Container: {
    width: '100%',
    padding: '20px',
    boxSizing: 'border-box'
  },
  intro7Image: {
    width: '100%',
    height: 'auto',
    maxWidth: '100%',
    display: 'block'
  }
};

const AppTop = () => {
  const navigate = useNavigate();
  
  const { objectIdAutoFlg, pixStorageFlg } = useAuth()

  // APIエラーハンドリングフック
  const { 
    showErrorDialog,
    errorMessage,
    handleApiError,
    closeErrorDialog
  } = useApiError(() => setIsLoading(false));
  
  // UI状態
  const [isLoading, setIsLoading] = useState(true); // ローディング状態
  const [showIntroduction, setShowIntroduction] = useState(false); // イントロ表示フラグ
  
  // カテゴリー関連
  const [satsueiMemoCategories, setSatsueiMemoCategories] = useState([]); // 撮影メモカテゴリー一覧
  const [currentSatsueiMemoCategoryId, setCurrentSatsueiMemoCategoryId] = useState(''); // 現在選択中のカテゴリーID
  
  // オブジェクトデータ関連
  const [objectList, setObjectList] = useState([]); // オブジェクト一覧
  const [limit] = useState(60); // 1回の読み込みで取得する件数
  const [offset, setOffset] = useState(0); // オフセット
  const [showLoadMoreButton, setShowLoadMoreButton] = useState(false); // 追加読み込みボタン表示フラグ
  
  // スクロール位置管理
  const [savedScrollPosition, setSavedScrollPosition] = useState(null); // 保存されたスクロール位置
  const [navScrollPosition, setNavScrollPosition] = useState(0); // ナビゲーションスクロール位置
  
  // 画像URLの管理（blob用）
  const [imageUrls, setImageUrls] = useState({}); // 画像URLキャッシュ
  
  // カテゴリタブのref
  const categoryRefs = useRef({}); // カテゴリタブの参照
  const navContainerRef = useRef(null); // ナビゲーションコンテナの参照

  // コンポーネント初期化
  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ウィンドウリサイズ処理（レスポンシブ対応）
  useEffect(() => {
    const handleResize = () => {
      // リサイズ時に強制的に再レンダリング
      setObjectList(prev => [...prev]);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // スクロール位置復元処理
  useEffect(() => {
    if (savedScrollPosition !== null) {
      const listContainer = document.querySelector('[data-scroll-container]');
      if (listContainer) {
        // 複数のフレームで試行して確実に復元
        const restoreScroll = () => {
          listContainer.scrollTop = savedScrollPosition;
          setSavedScrollPosition(null);
        };
        
        requestAnimationFrame(() => {
          requestAnimationFrame(restoreScroll);
        });
      }
    }
  }, [objectList, savedScrollPosition]);

  // ナビゲーションスクロール位置復元処理
  useEffect(() => {
    if (navScrollPosition > 0 && navContainerRef.current) {
      const restoreNavScroll = () => {
        if (navContainerRef.current) {
          navContainerRef.current.scrollLeft = navScrollPosition;
          // スクロール位置をリセット
          setNavScrollPosition(0);
        }
      };
      
      requestAnimationFrame(() => {
        requestAnimationFrame(restoreNavScroll);
      });
    }
  }, [currentSatsueiMemoCategoryId, navScrollPosition]);

  // レスポンシブスタイル計算
  const getResponsiveStyles = () => {
    return {
      intro7Container: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'flex-start',  // 上寄せに変更
        justifyContent: 'center',
        backgroundColor: 'white',
        zIndex: 1
      },
      intro7Image: {
        width: '100%',
        height: 'auto',
        maxHeight: '80vh',  // 最大高さを制限
        objectFit: 'contain',
        display: 'block'
      }
    };
  };
  
  // オブジェクトデータ読み込み処理
  const loadObjectData = useCallback(async (satsueiMemoCategoryId, currentOffset = 0) => {
    try {
      setIsLoading(true);
      const result = await mobileTopService.getObjectList(satsueiMemoCategoryId, limit, currentOffset);

      if(result.returnCode !== '200'){
        MessageDialog.show('エラー', '撮影データ一覧の取得に失敗しました。', null, 'error');
        return;
      }
      
      if (satsueiMemoCategoryId === 0 && result.objectDatas.length === 0) {
        // 初期使用時の案内
        setObjectList([]);
      } else {
        // objectIdで重複を除去し、orderIndex=0の画像のみを表示
        const uniqueObjectDatas = (result.objectDatas || []).reduce((acc, current) => {
          const existingIndex = acc.findIndex(item => item.objectId === current.objectId);
          if (existingIndex === -1) {
            // 新しいobjectIdの場合、orderIndex=0の画像のみを追加（orderIndexが存在しない場合は全て追加）
            if (current.orderIndex === 0 || current.orderIndex === undefined) {
              acc.push(current);
            }
          } else {
            // 既存のobjectIdの場合、orderIndex=0の画像で置き換え（orderIndexが存在しない場合は全て置き換え）
            if (current.orderIndex === 0 || current.orderIndex === undefined) {
              acc[existingIndex] = current;
            }
          }
          return acc;
        }, []);
        
        // 画像をblobとして取得（先に画像を読み込む）
        await loadImagesAsBlobs(uniqueObjectDatas);
        
        // 画像読み込み完了後にオブジェクトリストを設定
        if (currentOffset === 0) {
          setObjectList(uniqueObjectDatas);
        } else {
          setObjectList(prev => {
            // 既存の結果と新しい結果をマージし、重複を除去
            const allResults = [...prev, ...uniqueObjectDatas];
            const mergedResults = allResults.reduce((acc, current) => {
              const existingIndex = acc.findIndex(item => item.objectId === current.objectId);
              if (existingIndex === -1) {
                acc.push(current);
              } else {
                // 既存のobjectIdの場合、orderIndex=0の画像で置き換え（orderIndexが存在しない場合は全て置き換え）
                if (current.orderIndex === 0 || current.orderIndex === undefined) {
                  acc[existingIndex] = current;
                }
              }
              return acc;
            }, []);
            return mergedResults;
          });
        }
      }
      
      const newOffset = currentOffset + limit;
      setOffset(newOffset);
      const shouldShowLoadMore = parseInt(result.objectCount) > newOffset;
      setShowLoadMoreButton(shouldShowLoadMore);
      
      return result; // Promiseの解決値を返す
    } catch (error) {
      handleApiError(new Error('撮影データ一覧の取得に失敗しました。'));
    } finally {
      setIsLoading(false);
    }
  }, [limit, handleApiError]);

  // 撮影メモカテゴリー選択処理
  const selectSatsueiMemoCategory = useCallback(async(satsueiMemoCategoryId = '') => {
    // 選択されたカテゴリタブまでスクロール（状態更新前に実行）
    const categoryKey = satsueiMemoCategoryId || 'recent';
    const categoryElement = categoryRefs.current[categoryKey];
    let targetScrollLeft = 0;
    
    if (categoryElement && navContainerRef.current) {
      const navContainer = navContainerRef.current;
      const containerRect = navContainer.getBoundingClientRect();
      const elementRect = categoryElement.getBoundingClientRect();
      
      // 要素を中央に配置するためのスクロール位置を計算
      const elementCenter = elementRect.left + (elementRect.width / 2);
      const containerCenter = containerRect.left + (containerRect.width / 2);
      targetScrollLeft = navContainer.scrollLeft + (elementCenter - containerCenter);
    }
    
    // 状態更新
    setCurrentSatsueiMemoCategoryId(satsueiMemoCategoryId);
    setOffset(0);
    setShowLoadMoreButton(false);
    setObjectList([]);
    
    // データ読み込み
    await loadObjectData(satsueiMemoCategoryId, 0);
    
    // データ読み込み完了後にスクロール位置を復元
    if (targetScrollLeft > 0) {
      setNavScrollPosition(Math.max(0, targetScrollLeft));
    }
  }, [loadObjectData]);

  // 撮影メモカテゴリー読み込み処理
  const loadSatsueiMemoCategory = useCallback(async () => {
    try {
      const result = await mobileTopService.getSatsueiMemoCategory();

      if(result.returnCode !== '200'){
        MessageDialog.show('エラー', '撮影メモカテゴリーの取得に失敗しました。', null, 'error');
      }

      setSatsueiMemoCategories(result.satsueiMemoCategoryList || []);
      selectSatsueiMemoCategory();
    } catch (error) {
      handleApiError(new Error('撮影メモカテゴリーの取得に失敗しました。'));
    }
  }, [handleApiError, selectSatsueiMemoCategory]);

  // メイン読み込み処理
  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await mobileTopService.getIsDisplayIntro();
      
      // ビジネスロジックエラーのチェック
      if (result.returnCode !== '200') {
        MessageDialog.show('エラー', '初期化に失敗しました。', null, 'error');
        setIsLoading(false);
        return; // エラーが発生した場合は処理を終了
      }
      
      if (result.isDisplayIntro === 1) {
        // アプリアプデート用ダイアログ（現在は無効化）
        setShowIntroduction(false);
        
        // ビジネスロジックエラーのチェック
        if (!(pixStorageFlg === 0 || pixStorageFlg === 1)) {
          handleApiError(new Error('初期化に失敗しました。'));
          return; // エラーが発生した場合は処理を終了
        }
        
        if (pixStorageFlg === 1) {
          await loadSatsueiMemoCategory();
        } else {
          // pixStorageFlgが0の場合はローディングを終了
          setIsLoading(false);
        }
      } else {
        setShowIntroduction(false);
        
        // ビジネスロジックエラーのチェック
        if (!(pixStorageFlg === 0 || pixStorageFlg === 1)) {
          handleApiError(new Error('初期化に失敗しました。'));
          return; // エラーが発生した場合は処理を終了
        }
        
        if (pixStorageFlg === 1) {
          await loadSatsueiMemoCategory();
        } else {
          // pixStorageFlgが0の場合はローディングを終了
          setIsLoading(false);
        }
      }
    } catch (error) {
      handleApiError(new Error('初期化に失敗しました。'));
    } finally {
      // ローディング終了はloadObjectData内で行われるため、ここでは実行しない
      // setIsLoading(false);
    }
  }, [handleApiError, loadSatsueiMemoCategory, pixStorageFlg]);

  // イントロ表示登録処理
  const loadRegistIsDisplayIntro = useCallback(async () => {
    try {
      const result = await mobileTopService.registIsDisplayIntro(0);

      if(result.returnCode !== '200'){
        MessageDialog.show('エラー', '登録に失敗しました。', null, 'error');
        return;
      }

      navigate('/appTop');
    } catch (error) {
      handleApiError(new Error('登録に失敗しました。'));
    }
  }, [handleApiError, navigate]);

  // オブジェクト詳細表示処理
  const handleObjectClick = (objectId) => {
    navigate(`/appObjectDetail?objectId=${objectId}&objectIdAutoFlg=${objectIdAutoFlg}&from=top`);
  };

  // 画像blob取得処理
  const loadImagesAsBlobs = async (objectDatas) => {
    const imagePromises = objectDatas.map(async (object) => {
      if (object.imageCount > 0 && object.objectImageId !== undefined 
        && object.objectImageId !== null && object.objectImageId !== '') {
        try {
          const blobUrl = await mobileObjectDetailService.getImageAsBlob(
            object.objectId,
            object.objectImageId,
            object.maxObjectImageEditId,
            object.filenameExtension,
            object.isUploaded
          );
          return { objectId: object.objectId, blobUrl };
        } catch (error) {
          return { objectId: object.objectId, blobUrl: '/core/img/errorimage.png' };
        }
      }
      return { objectId: object.objectId, blobUrl: '/core/img/misatsuei.png' };
    });

    try {
      const imageResults = await Promise.all(imagePromises);
      const newImageUrls = {};
      imageResults.forEach(({ objectId, blobUrl }) => {
        newImageUrls[objectId] = blobUrl;
      });
      
      setImageUrls(prev => ({ ...prev, ...newImageUrls }));
    } catch (error) {
      // 画像読み込みエラーは無視
    }
  };

  // 追加読み込み処理
  const handleLoadMore = async () => {
    // 現在のスクロール位置を保存
    const listContainer = document.querySelector('[data-scroll-container]');
    const currentScrollTop = listContainer ? listContainer.scrollTop : 0;
    
    try {
      // ローディング状態を設定せずに直接データを取得
      const result = await mobileTopService.getObjectList(currentSatsueiMemoCategoryId, limit, offset);

      if(result.returnCode !== '200'){
        MessageDialog.show('エラー', '撮影データ一覧の取得に失敗しました。', null, 'error');
        return;
      }
      
      if (result.objectDatas.length > 0) {
        // objectIdで重複を除去し、orderIndex=0の画像のみを表示
        const uniqueObjectDatas = (result.objectDatas || []).reduce((acc, current) => {
          const existingIndex = acc.findIndex(item => item.objectId === current.objectId);
          if (existingIndex === -1) {
            // 新しいobjectIdの場合、orderIndex=0の画像のみを追加（orderIndexが存在しない場合は全て追加）
            if (current.orderIndex === 0 || current.orderIndex === undefined) {
              acc.push(current);
            }
          } else {
            // 既存のobjectIdの場合、orderIndex=0の画像で置き換え（orderIndexが存在しない場合は全て置き換え）
            if (current.orderIndex === 0 || current.orderIndex === undefined) {
              acc[existingIndex] = current;
            }
          }
          return acc;
        }, []);
        
        if (uniqueObjectDatas.length > 0) {
          // スクロール位置を保存
          setSavedScrollPosition(currentScrollTop);
          
          // 画像をblobとして取得（先に画像を読み込む）
          await loadImagesAsBlobs(uniqueObjectDatas);
          
          // 画像読み込み完了後にオブジェクトリストを更新
          setObjectList(prev => {
            const allResults = [...prev, ...uniqueObjectDatas];
            const mergedResults = allResults.reduce((acc, current) => {
              const existingIndex = acc.findIndex(item => item.objectId === current.objectId);
              if (existingIndex === -1) {
                acc.push(current);
              } else {
                // 既存のobjectIdの場合、orderIndex=0の画像で置き換え（orderIndexが存在しない場合は全て置き換え）
                if (current.orderIndex === 0 || current.orderIndex === undefined) {
                  acc[existingIndex] = current;
                }
              }
              return acc;
            }, []);
            return mergedResults;
          });
        }
        
        // offsetを更新
        const newOffset = offset + limit;
        setOffset(newOffset);
        
        // さらに読み込むボタンの表示状態を更新
        const shouldShowLoadMore = parseInt(result.objectCount) > newOffset;
        setShowLoadMoreButton(shouldShowLoadMore);
      }
    } catch (error) {
      handleApiError(new Error('撮影データ一覧の取得に失敗しました。'));
    }
  };

  // オブジェクトリストレンダリング
  const renderObjectList = () => {
    
    // レスポンシブ対応：画面幅に応じて画像サイズを調整
    const screenWidth = window.innerWidth;
    const horizontalNum = 3;
    const containerPadding = 0; // 左右のパディングを0に設定
    const gap = 0; // 画像間のギャップを0に設定
    const availableWidth = screenWidth - containerPadding - (gap * (horizontalNum - 1));
    
    return objectList.map((object, index) => {
      const imageWidth = availableWidth / horizontalNum;
      return (
        <div 
          key={`${object.objectId}-${index}`}
          style={{
            ...styles.objectDIV,
            width: `${imageWidth}px`
          }}
          onClick={() => handleObjectClick(object.objectId)}
        >
          <div style={styles.objectIdDIV}>{object.objectId}</div>
          <div style={styles.objectInDIV}>
            <img 
              src={imageUrls[object.objectId] || '/core/img/errorimage.png'} 
              alt={`Object ${object.objectId}`}
              style={styles.objectInDIVImg}
              onError={(e) => {
                e.target.src = '/core/img/errorimage.png';
              }}
            />
          </div>
        </div>
      );
    });
  };

  // 撮影メモカテゴリーセレクターレンダリング
  const renderSatsueiMemoCategorySelector = () => {
    // 旧実装に合わせ、カテゴリ数が「1」のときのみ非表示
    // または、オブジェクトデータが0件の場合は非表示
    if (satsueiMemoCategories.length === 1 && objectList.length === 0) {
      return null;
    }

    return (
      <nav style={styles.navHorizontal}>
        <div style={styles.mask} ref={navContainerRef}>
          <ul style={styles.list}>
            <li 
              ref={(el) => { categoryRefs.current['recent'] = el; }}
              style={{
                ...styles.listLi,
                backgroundColor: currentSatsueiMemoCategoryId === '' ? '#F64349' : '#EEE',
                color: currentSatsueiMemoCategoryId === '' ? '#FFF' : '#444'
              }}
              onClick={() => selectSatsueiMemoCategory('')}
            >
              最近撮影した写真
            </li>
            {satsueiMemoCategories.map(category => (
              <li
                key={category.satsueiMemoCategoryId}
                ref={(el) => { categoryRefs.current[category.satsueiMemoCategoryId] = el; }}
                style={{
                  ...styles.listLi,
                  backgroundColor: currentSatsueiMemoCategoryId === category.satsueiMemoCategoryId ? '#F64349' : '#EEE',
                  color: currentSatsueiMemoCategoryId === category.satsueiMemoCategoryId ? '#FFF' : '#444'
                }}
                onClick={() => selectSatsueiMemoCategory(category.satsueiMemoCategoryId)}
              >
                {category.satsueiMemoCategoryName}
              </li>
            ))}
          </ul>
                </div>
      </nav>
    );
  };

  if (isLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>
          読み込み中...
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* イントロ表示 */}
      {showIntroduction && (
        <div style={styles.introduction}>
          <ul style={styles.introductionSlider}>
            <li style={styles.introductionSliderLi}>
              <img src="/core/img/intro/intro1.jpg" style={styles.introductionSliderImg} alt="Intro 1" />
            </li>
            <li style={styles.introductionSliderLi}>
              <img src="/core/img/intro/intro2.jpg" style={styles.introductionSliderImg} alt="Intro 2" />
            </li>
            <li style={styles.introductionSliderLi}>
              <img src="/core/img/intro/intro3.jpg" style={styles.introductionSliderImg} alt="Intro 3" />
            </li>
            <li style={styles.introductionSliderLi}>
              <img src="/core/img/intro/intro4.jpg" style={styles.introductionSliderImg} alt="Intro 4" />
            </li>
            <li style={styles.introductionSliderLi}>
              <img src="/core/img/intro/intro5.jpg" style={styles.introductionSliderImg} alt="Intro 5" />
            </li>
            <li style={styles.introductionSliderLi}>
              <img src="/core/img/intro/intro6.jpg" style={styles.introductionSliderImg} alt="Intro 6" />
              <button 
                style={styles.startButton}
                onClick={loadRegistIsDisplayIntro}
              >
                開始する
              </button>
            </li>
          </ul>
                  </div>
      )}

      {/* メインコンテンツ */}
      {!showIntroduction && (
        <div style={styles.contents}>
        {pixStorageFlg === 0 ? (
            // pixStorageFlg=0の場合の表示
            <>
              <img src="/core/img/pix-label_logo.png" style={styles.pixLabelLogo} alt="PIX Label Logo" />
              
              <div style={styles.news}>
                <div style={styles.newsTitle}>新着情報</div>
                <div style={styles.newsNaiyo}>
                  2019/4/1 pix-labelリリースしました。
                </div>
              </div>

              <img src="/core/img/top_info_1.png" style={{...styles.topInfo, ...styles.topInfo1}} alt="Top Info 1" />
              <img src="/core/img/top_info_2.png" style={{...styles.topInfo, ...styles.topInfo2}} alt="Top Info 2" />
              <img src="/core/img/top_info_3.png" style={{...styles.topInfo, ...styles.topInfo3}} alt="Top Info 3" />
              <img src="/core/img/top_info_4.png" style={{...styles.topInfo, ...styles.topInfo4}} alt="Top Info 4" />
              <img src="/core/img/top_info_5.png" style={{...styles.topInfo, ...styles.topInfo5}} alt="Top Info 5" />
              <img src="/core/img/top_info_6.png" style={{...styles.topInfo, ...styles.topInfo6}} alt="Top Info 6" />
            </>
          ) : (
            // pixStorageFlg=1の場合の表示
            <>
              {/* 撮影メモカテゴリセレクター */}
              {renderSatsueiMemoCategorySelector()}
              
              {/* オブジェクトリスト */}
              <div style={{
                ...styles.listContainer,
                // オブジェクトデータが0件の場合は高さを100vhに
                height: (satsueiMemoCategories.length === 1 && objectList.length === 0) ? '100vh' : '100%'
              }} data-scroll-container>
                <div style={{
                  ...styles.objectList,
                  // タブが表示される場合は上部に60pxマージン（固定ヘッダー分）
                  // オブジェクトデータが0件かつカテゴリ1件の場合はマージンを0に（ヘッダー非表示のため）
                  margin: (satsueiMemoCategories.length === 1 && objectList.length === 0) ? '0' : '60px auto 120px auto'
                }}>
                  {satsueiMemoCategories.length === 1 && objectList.length === 0 ? (
                    <div style={getResponsiveStyles().intro7Container}>
                      <img src="/core/img/intro/intro7.jpg" alt="Intro 7" style={getResponsiveStyles().intro7Image} />
                    </div>
                  ) : (
                    renderObjectList()
                  )}
                </div>
                
                {/* さらに読み込むボタン */}
                {showLoadMoreButton && (
                  <div style={styles.loadObjectDataButtonArea}>
                    <button 
                      style={styles.loadObjectDataButton}
                      onClick={handleLoadMore}
                    >
                      さらに読み込む
                    </button>
                  </div>
                )}
              </div>
            </>
                     )}
        </div>
      )}

      {/* ボトムナビゲーション */}
      <Footer />

      {/* Error Dialog */}
      <MessageDialog
        isOpen={showErrorDialog}
        onClose={closeErrorDialog}
        title="メッセージ"
        message={errorMessage}
        type="error"
      />
    </div>
  );
};

export default AppTop;

